import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  MenuItem,
  Avatar,
  CircularProgress,
  Alert,
  Checkbox,
  Toolbar,
  Tooltip,
  Snackbar,
  ListItemIcon,
  ListItemText,
  Divider,
  Drawer,
  FormGroup,
  FormControlLabel,
  Slider,
  Select,
  FormControl,
  InputLabel,
  Switch,
  Collapse,
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  PlayArrow,
  Download,
  Delete,
  LibraryMusic,
  Album,
  AudioFile,
  CheckCircle,
  Error,
  Close,
  Edit,
  Refresh,
  Fingerprint,
  GraphicEq,
  LocalOffer,
  AutoAwesome,
  MusicNote,
  Lyrics,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

import {
  useContentList,
  useContentDelete,
  useContentApproval,
  useContentUpdate,
  useContentSearch,
} from '../hooks/useContent';
import {
  Content,
  ContentWorkflowState,
  ContentType,
  ListContentRequest,
  ContentSearchParams,
  formatFileSize,
  formatDuration,
  ContentService,
} from '../api';
import AudioPreview from '../components/AudioPreview';
import { MetadataEditor, BulkTagSelector } from '../components/ContentLibrary';
import { TagService, type Tag } from '../api';

// Format filter options
const FORMAT_OPTIONS = [
  { label: 'All Formats', value: '' },
  { label: 'MP3', value: 'mp3' },
  { label: 'WAV', value: 'wav' },
  { label: 'FLAC', value: 'flac' },
  { label: 'MP4', value: 'mp4' },
];

// Musical key options
const KEY_OPTIONS = [
  '', 'C', 'Cm', 'C#', 'C#m', 'D', 'Dm', 'D#', 'D#m',
  'E', 'Em', 'F', 'Fm', 'F#', 'F#m', 'G', 'Gm',
  'G#', 'G#m', 'A', 'Am', 'A#', 'A#m', 'B', 'Bm',
];

// Mood options for filter
const MOOD_OPTIONS = [
  '', 'upbeat', 'chill', 'energetic', 'mellow', 'dramatic', 'romantic',
  'melancholy', 'inspiring', 'nostalgic', 'dreamy', 'dark', 'bright',
  'aggressive', 'peaceful', 'groovy', 'funky', 'anthemic', 'cinematic', 'epic',
];

type ReprocessMode = 'id3' | 'identify' | 'audio' | 'full';

const ContentLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<Content | null>(null);
  const [previewContent, setPreviewContent] = useState<Content | null>(null);
  const [editContent, setEditContent] = useState<Content | null>(null);
  const [filters, setFilters] = useState({
    workflow_state: [] as ContentWorkflowState[],
    content_type: [] as ContentType[],
  });
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reprocessMenuAnchor, setReprocessMenuAnchor] = useState<null | HTMLElement>(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Tag filter state
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [showTagFilter, setShowTagFilter] = useState(false);

  // Extended search filters
  const [useAdvancedSearch] = useState(false);
  const [lyricsSearch, setLyricsSearch] = useState('');
  const [bpmRange, setBpmRange] = useState<[number, number]>([60, 200]);
  const [bpmEnabled, setBpmEnabled] = useState(false);
  const [selectedKey, setSelectedKey] = useState('');
  const [selectedMood, setSelectedMood] = useState('');
  const [energyRange, setEnergyRange] = useState<[number, number]>([1, 10]);
  const [energyEnabled, setEnergyEnabled] = useState(false);
  const [aiOnly, setAiOnly] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Load available tags on mount
  React.useEffect(() => {
    const loadTags = async () => {
      try {
        const data = await TagService.getTags();
        setAvailableTags(data.tags);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    loadTags();
  }, []);

  // Build query parameters for standard list
  const queryParams: ListContentRequest = useMemo(() => ({
    page: page + 1, // API uses 1-based pagination
    size: rowsPerPage,
    filters: {
      search: searchQuery || undefined,
      format: selectedFormat || undefined,
      workflow_state: filters.workflow_state.length > 0 ? filters.workflow_state : undefined,
      content_type: filters.content_type.length > 0 ? filters.content_type : undefined,
      tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    },
    sort: {
      field: 'created_at',
      direction: 'desc',
    },
  }), [page, rowsPerPage, searchQuery, selectedFormat, filters, selectedTagIds]);

  // Build advanced search parameters
  const searchParams: ContentSearchParams = useMemo(() => ({
    q: lyricsSearch || searchQuery || undefined,
    page: page + 1,
    size: rowsPerPage,
    tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
    bpm_min: bpmEnabled ? bpmRange[0] : undefined,
    bpm_max: bpmEnabled ? bpmRange[1] : undefined,
    key: selectedKey || undefined,
    mood: selectedMood || undefined,
    energy_min: energyEnabled ? energyRange[0] : undefined,
    energy_max: energyEnabled ? energyRange[1] : undefined,
    ai_only: aiOnly || undefined,
    format: selectedFormat as 'mp3' | 'wav' | 'flac' | 'mp4' | undefined,
  }), [page, rowsPerPage, lyricsSearch, searchQuery, selectedTagIds, bpmEnabled, bpmRange, selectedKey, selectedMood, energyEnabled, energyRange, aiOnly, selectedFormat]);

  // Check if advanced search should be used
  const shouldUseAdvancedSearch = useMemo<boolean>(() => {
    return Boolean(useAdvancedSearch || lyricsSearch || bpmEnabled || selectedKey || selectedMood || energyEnabled || aiOnly);
  }, [useAdvancedSearch, lyricsSearch, bpmEnabled, selectedKey, selectedMood, energyEnabled, aiOnly]);

  // Handle format filter change
  const handleFormatChange = (format: string) => {
    setSelectedFormat(format);
    setPage(0); // Reset to first page when filter changes
  };

  // Handle tag filter change
  const handleTagFilterToggle = (tagId: string) => {
    setSelectedTagIds((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });
    setPage(0);
  };

  const clearTagFilters = () => {
    setSelectedTagIds([]);
    setPage(0);
  };

  // Handle workflow state filter toggle
  const handleWorkflowStateToggle = (state: ContentWorkflowState) => {
    setFilters((prev) => ({
      ...prev,
      workflow_state: prev.workflow_state.includes(state)
        ? prev.workflow_state.filter((s) => s !== state)
        : [...prev.workflow_state, state],
    }));
    setPage(0);
  };

  // Handle content type filter toggle
  const handleContentTypeToggle = (type: ContentType) => {
    setFilters((prev) => ({
      ...prev,
      content_type: prev.content_type.includes(type)
        ? prev.content_type.filter((t) => t !== type)
        : [...prev.content_type, type],
    }));
    setPage(0);
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({ workflow_state: [], content_type: [] });
    setSelectedFormat('');
    setSelectedTagIds([]);
    setSearchQuery('');
    setLyricsSearch('');
    setBpmEnabled(false);
    setBpmRange([60, 200]);
    setSelectedKey('');
    setSelectedMood('');
    setEnergyEnabled(false);
    setEnergyRange([1, 10]);
    setAiOnly(false);
    setPage(0);
  };

  // Count active filters
  const activeFilterCount =
    filters.workflow_state.length +
    filters.content_type.length +
    (selectedFormat ? 1 : 0) +
    selectedTagIds.length +
    (lyricsSearch ? 1 : 0) +
    (bpmEnabled ? 1 : 0) +
    (selectedKey ? 1 : 0) +
    (selectedMood ? 1 : 0) +
    (energyEnabled ? 1 : 0) +
    (aiOnly ? 1 : 0);

  // Hooks - use advanced search when filters are active
  const { data: contentData, isLoading: listLoading, error: listError, refetch: listRefetch } = useContentList(queryParams);
  const { data: searchData, isLoading: searchLoading, error: searchError, refetch: searchRefetch } = useContentSearch(searchParams, shouldUseAdvancedSearch);

  // Use the appropriate data source based on search mode
  const isLoading = shouldUseAdvancedSearch ? searchLoading : listLoading;
  const error = shouldUseAdvancedSearch ? searchError : listError;
  const refetch = shouldUseAdvancedSearch ? searchRefetch : listRefetch;

  const deleteMutation = useContentDelete();
  const updateMutation = useContentUpdate();
  const { approve, reject, isApproving, isRejecting } = useContentApproval();

  // Get content list for navigation
  const contentList = shouldUseAdvancedSearch
    ? (searchData?.items || [])
    : (contentData?.items || []);

  // Find current edit index for prev/next navigation
  const currentEditIndex = editContent
    ? contentList.findIndex((c) => c.id === editContent.id)
    : -1;

  const handleMetadataSave = async (id: string, metadata: any) => {
    return new Promise<void>((resolve, reject) => {
      updateMutation.mutate(
        { id, updates: metadata },
        {
          onSuccess: () => {
            refetch();
            resolve();
          },
          onError: (err: any) => {
            reject(err);
          },
        }
      );
    });
  };

  const handleMetadataNavigate = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && currentEditIndex > 0) {
      setEditContent(contentList[currentEditIndex - 1]);
    } else if (direction === 'next' && currentEditIndex < contentList.length - 1) {
      setEditContent(contentList[currentEditIndex + 1]);
    }
  };

  // Event handlers
  const handlePlay = (content: Content) => {
    setPreviewContent(content);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, content: Content) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(content);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  const handleDelete = () => {
    if (selectedItem) {
      deleteMutation.mutate(selectedItem.id, {
        onSuccess: () => {
          refetch();
          handleMenuClose();
        }
      });
    }
  };

  const handleApprove = () => {
    if (selectedItem) {
      approve({ id: selectedItem.id }, {
        onSuccess: () => {
          refetch();
          handleMenuClose();
        }
      });
    }
  };

  const handleReject = () => {
    if (selectedItem) {
      const reason = prompt('Please provide a reason for rejection:');
      if (reason) {
        reject({ id: selectedItem.id, reason }, {
          onSuccess: () => {
            refetch();
            handleMenuClose();
          }
        });
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: ContentWorkflowState) => {
    switch (status) {
      case ContentWorkflowState.APPROVED: return 'success';
      case ContentWorkflowState.PROCESSING: return 'warning';
      case ContentWorkflowState.REVIEW: return 'info';
      case ContentWorkflowState.REJECTED: return 'error';
      case ContentWorkflowState.UPLOADED: return 'default';
      default: return 'default';
    }
  };

  const getFormatIcon = (mimeType: string) => {
    if (mimeType.includes('mp3')) return <AudioFile sx={{ color: '#4a9eff' }} />;
    if (mimeType.includes('wav')) return <LibraryMusic sx={{ color: '#10b981' }} />;
    if (mimeType.includes('flac')) return <Album sx={{ color: '#ff6b35' }} />;
    return <AudioFile sx={{ color: '#999' }} />;
  };

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(new Set(content.map(item => item.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Reprocess handlers
  const handleReprocessContent = async (id: string, mode: ReprocessMode, replaceArtwork = false) => {
    try {
      await ContentService.reprocessContent(id, mode, { replaceArtwork });
      setSnackbarMessage('Reprocessing queued successfully');
      setSnackbarOpen(true);
      refetch();
    } catch (err: any) {
      setSnackbarMessage(err.message || 'Failed to start reprocessing');
      setSnackbarOpen(true);
    }
  };

  const handleBulkReprocess = async (mode: ReprocessMode, replaceArtwork = false) => {
    setReprocessMenuAnchor(null);
    if (selectedIds.size === 0) return;

    setBulkProcessing(true);
    try {
      const result = await ContentService.bulkReprocess(
        Array.from(selectedIds),
        mode,
        { replaceArtwork },
      );
      const modeLabels: Record<ReprocessMode, string> = {
        id3: 'ID3 extraction',
        identify: 'Track identification',
        audio: 'Audio analysis',
        full: 'Full analysis',
      };
      const action = replaceArtwork ? 'Artwork replacement' : modeLabels[mode];
      setSnackbarMessage(`${action}: ${result.successful}/${result.total} queued successfully`);
      setSnackbarOpen(true);
      clearSelection();
      refetch();
    } catch (err: any) {
      setSnackbarMessage(err.message || 'Failed to start bulk reprocessing');
      setSnackbarOpen(true);
    } finally {
      setBulkProcessing(false);
    }
  };

  // Handle loading and error states
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Failed to load content: {(error as any)?.message || 'Unknown error'}
      </Alert>
    );
  }

  const content = shouldUseAdvancedSearch
    ? (searchData?.items || [])
    : (contentData?.items || []);
  const totalCount = shouldUseAdvancedSearch
    ? (searchData?.total || 0)
    : (contentData?.total || 0);

  // Selection state helpers (must be after content is defined)
  const isAllSelected = content.length > 0 && selectedIds.size === content.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < content.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Content Library
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" startIcon={<LibraryMusic />} onClick={() => navigate('/scheduling/playlists')}>
            New Playlist
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterDrawerOpen(true)}
            color={activeFilterCount > 0 ? 'primary' : 'inherit'}
          >
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 400 }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {FORMAT_OPTIONS.map((format) => (
              <Chip
                key={format.value}
                label={format.label}
                clickable
                variant={selectedFormat === format.value ? 'filled' : 'outlined'}
                color={selectedFormat === format.value ? 'primary' : 'default'}
                onClick={() => handleFormatChange(format.value)}
              />
            ))}
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Chip
              icon={<LocalOffer />}
              label={selectedTagIds.length > 0 ? `Tags (${selectedTagIds.length})` : 'Tags'}
              clickable
              variant={showTagFilter ? 'filled' : 'outlined'}
              color={selectedTagIds.length > 0 ? 'secondary' : 'default'}
              onClick={() => setShowTagFilter(!showTagFilter)}
            />
            {shouldUseAdvancedSearch && (
              <Chip
                icon={<AutoAwesome />}
                label="Advanced Search"
                size="small"
                color="primary"
                variant="filled"
                sx={{ ml: 1 }}
              />
            )}
            {aiOnly && (
              <Chip
                icon={<AutoAwesome />}
                label="AI Only"
                size="small"
                color="secondary"
                variant="filled"
              />
            )}
          </Box>
        </Box>

        {/* Tag Filter Section */}
        {showTagFilter && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Filter by Tags
              </Typography>
              {selectedTagIds.length > 0 && (
                <Button size="small" onClick={clearTagFilters}>
                  Clear All
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {availableTags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  clickable
                  onClick={() => handleTagFilterToggle(tag.id)}
                  sx={{
                    bgcolor: selectedTagIds.includes(tag.id) ? tag.color : tag.color + '20',
                    borderColor: tag.color,
                    border: '1px solid',
                    color: selectedTagIds.includes(tag.id) ? 'white' : 'inherit',
                    '&:hover': {
                      bgcolor: selectedTagIds.includes(tag.id) ? tag.color : tag.color + '40',
                    },
                  }}
                />
              ))}
              {availableTags.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No tags available. Create tags in the settings.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Bulk Action Toolbar */}
        {selectedIds.size > 0 && (
          <Toolbar
            sx={{
              bgcolor: 'primary.dark',
              borderRadius: 1,
              mb: 2,
              pl: 2,
              pr: 1,
            }}
          >
            <Typography sx={{ flex: 1 }} color="inherit" variant="subtitle1">
              {selectedIds.size} selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <BulkTagSelector
                selectedContentIds={Array.from(selectedIds)}
                onComplete={() => {
                  setSnackbarMessage('Tags applied successfully');
                  setSnackbarOpen(true);
                  refetch();
                }}
              />
              <Tooltip title="Reprocess selected">
                <Button
                  color="inherit"
                  onClick={(e) => setReprocessMenuAnchor(e.currentTarget)}
                  disabled={bulkProcessing}
                  startIcon={bulkProcessing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                  size="small"
                >
                  Reprocess
                </Button>
              </Tooltip>
              <Tooltip title="Clear selection">
                <IconButton color="inherit" onClick={clearSelection} size="small">
                  <Close />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        )}

        {/* Bulk Reprocess Menu */}
        <Menu
          anchorEl={reprocessMenuAnchor}
          open={Boolean(reprocessMenuAnchor)}
          onClose={() => setReprocessMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleBulkReprocess('id3')}>
            <ListItemIcon>
              <LibraryMusic fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Re-read ID3 Tags"
              secondary="Fast, extracts embedded metadata"
            />
          </MenuItem>
          <MenuItem onClick={() => handleBulkReprocess('identify')}>
            <ListItemIcon>
              <Fingerprint fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Identify Tracks"
              secondary={`ACRCloud (uses ${selectedIds.size} of quota)`}
            />
          </MenuItem>
          <MenuItem onClick={() => handleBulkReprocess('audio')}>
            <ListItemIcon>
              <GraphicEq fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Rescan Audio"
              secondary="Bitrate, loudness, energy analysis"
            />
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleBulkReprocess('full')}>
            <ListItemIcon>
              <Refresh fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Full Analysis"
              secondary="All of the above"
            />
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleBulkReprocess('identify', true)}>
            <ListItemIcon>
              <Album fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Replace Artwork (force)"
              secondary="Discard current cover, fetch official from MusicBrainz"
            />
          </MenuItem>
        </Menu>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell width={40}></TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Format</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plays</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {content.map((item) => (
                <TableRow
                  key={item.id}
                  hover
                  selected={selectedIds.has(item.id)}
                  onDoubleClick={() => setEditContent(item)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(item.id)}
                      onChange={() => handleSelectOne(item.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      sx={{ color: '#4a9eff' }}
                      onClick={() => handlePlay(item)}
                    >
                      <PlayArrow />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          mr: 2,
                          bgcolor: '#2d2d2d',
                        }}
                      >
                        {getFormatIcon(item.mime_type)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.title || item.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.artist || (item.uploaded_by as any)?.name || 'Unknown'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.mime_type.split('/')[1]?.toUpperCase() || 'UNKNOWN'}
                      size="small"
                      sx={{
                        bgcolor: '#2d2d2d',
                        color: 'text.primary',
                        fontFamily: 'monospace',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {item.duration ? formatDuration(item.duration) : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(item.file_size)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.workflow_state}
                      size="small"
                      color={getStatusColor(item.workflow_state) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.play_count?.toLocaleString() || '0'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(item.upload_date || item.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, item)}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedItem && handlePlay(selectedItem)}>
          <PlayArrow sx={{ mr: 1 }} fontSize="small" />
          Play
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedItem) {
              setEditContent(selectedItem);
              handleMenuClose();
            }
          }}
        >
          <Edit sx={{ mr: 1 }} fontSize="small" />
          Edit Metadata
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedItem) {
              // Open download link in new tab
              window.open(`/api/content/${selectedItem.id}/download`, '_blank');
              handleMenuClose();
            }
          }}
        >
          <Download sx={{ mr: 1 }} fontSize="small" />
          Download
        </MenuItem>
        {selectedItem?.workflow_state === ContentWorkflowState.REVIEW && (
          <>
            <MenuItem onClick={handleApprove} disabled={isApproving}>
              <CheckCircle sx={{ mr: 1 }} fontSize="small" />
              Approve
            </MenuItem>
            <MenuItem onClick={handleReject} disabled={isRejecting}>
              <Error sx={{ mr: 1 }} fontSize="small" />
              Reject
            </MenuItem>
          </>
        )}
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Audio Preview Dialog */}
      {previewContent && (
        <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000, maxWidth: 400 }}>
          <AudioPreview
            src={ContentService.getStreamUrl(previewContent.id)}
            title={previewContent.title || previewContent.filename}
            artist={previewContent.artist}
            duration={previewContent.duration}
          />
          <IconButton
            sx={{ position: 'absolute', top: 8, right: 8 }}
            onClick={() => setPreviewContent(null)}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Metadata Editor Dialog */}
      <MetadataEditor
        open={!!editContent}
        content={editContent}
        onClose={() => setEditContent(null)}
        onSave={handleMetadataSave}
        onReprocess={handleReprocessContent}
        onNavigate={handleMetadataNavigate}
        canNavigatePrev={currentEditIndex > 0}
        canNavigateNext={currentEditIndex < contentList.length - 1}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 360 },
            bgcolor: 'rgba(30, 30, 30, 0.98)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>

          {/* Workflow State Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              Status
            </Typography>
            <FormGroup>
              {Object.values(ContentWorkflowState).map((state) => (
                <FormControlLabel
                  key={state}
                  control={
                    <Checkbox
                      checked={filters.workflow_state.includes(state)}
                      onChange={() => handleWorkflowStateToggle(state)}
                      size="small"
                    />
                  }
                  label={
                    <Chip
                      label={state.charAt(0).toUpperCase() + state.slice(1)}
                      size="small"
                      color={
                        state === ContentWorkflowState.APPROVED ? 'success' :
                        state === ContentWorkflowState.PROCESSING ? 'warning' :
                        state === ContentWorkflowState.REVIEW ? 'info' :
                        state === ContentWorkflowState.REJECTED ? 'error' : 'default'
                      }
                      variant={filters.workflow_state.includes(state) ? 'filled' : 'outlined'}
                    />
                  }
                  sx={{ mb: 0.5 }}
                />
              ))}
            </FormGroup>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Content Type Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              Content Type
            </Typography>
            <FormGroup>
              {Object.values(ContentType).map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={filters.content_type.includes(type)}
                      onChange={() => handleContentTypeToggle(type)}
                      size="small"
                    />
                  }
                  label={type.charAt(0).toUpperCase() + type.slice(1)}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </FormGroup>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Format Filter */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              Format
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {FORMAT_OPTIONS.filter(f => f.value).map((format) => (
                <Chip
                  key={format.value}
                  label={format.label}
                  clickable
                  variant={selectedFormat === format.value ? 'filled' : 'outlined'}
                  color={selectedFormat === format.value ? 'primary' : 'default'}
                  onClick={() => handleFormatChange(format.value === selectedFormat ? '' : format.value)}
                />
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Tag Filter */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Tags
              </Typography>
              {selectedTagIds.length > 0 && (
                <Button size="small" onClick={clearTagFilters}>
                  Clear
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {availableTags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  clickable
                  onClick={() => handleTagFilterToggle(tag.id)}
                  sx={{
                    bgcolor: selectedTagIds.includes(tag.id) ? tag.color : tag.color + '20',
                    borderColor: tag.color,
                    border: '1px solid',
                    color: selectedTagIds.includes(tag.id) ? 'white' : 'inherit',
                    '&:hover': {
                      bgcolor: selectedTagIds.includes(tag.id) ? tag.color : tag.color + '40',
                    },
                  }}
                />
              ))}
              {availableTags.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No tags available
                </Typography>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Advanced Search Filters */}
          <Box sx={{ mb: 3 }}>
            <Button
              fullWidth
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              endIcon={showAdvancedFilters ? <ExpandLess /> : <ExpandMore />}
              sx={{ justifyContent: 'space-between', mb: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoAwesome fontSize="small" />
                Advanced Search
              </Box>
            </Button>

            <Collapse in={showAdvancedFilters}>
              <Box sx={{ mt: 2 }}>
                {/* Lyrics/Full-text Search */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Search in lyrics & style prompts
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search lyrics, style prompts..."
                    value={lyricsSearch}
                    onChange={(e) => setLyricsSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lyrics fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* AI Generated Only */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={aiOnly}
                      onChange={(e) => setAiOnly(e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AutoAwesome fontSize="small" />
                      AI Generated Only
                    </Box>
                  }
                  sx={{ mb: 2 }}
                />

                {/* BPM Range */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      BPM Range
                    </Typography>
                    <Switch
                      size="small"
                      checked={bpmEnabled}
                      onChange={(e) => setBpmEnabled(e.target.checked)}
                    />
                  </Box>
                  <Slider
                    value={bpmRange}
                    onChange={(_, value) => setBpmRange(value as [number, number])}
                    valueLabelDisplay="auto"
                    min={60}
                    max={200}
                    disabled={!bpmEnabled}
                    marks={[
                      { value: 60, label: '60' },
                      { value: 120, label: '120' },
                      { value: 200, label: '200' },
                    ]}
                    sx={{ mt: 1 }}
                  />
                </Box>

                {/* Musical Key */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Musical Key</InputLabel>
                  <Select
                    value={selectedKey}
                    onChange={(e) => setSelectedKey(e.target.value)}
                    label="Musical Key"
                    startAdornment={
                      <InputAdornment position="start">
                        <MusicNote fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">Any Key</MenuItem>
                    {KEY_OPTIONS.filter(k => k).map((key) => (
                      <MenuItem key={key} value={key}>{key}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Mood */}
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Mood</InputLabel>
                  <Select
                    value={selectedMood}
                    onChange={(e) => setSelectedMood(e.target.value)}
                    label="Mood"
                  >
                    <MenuItem value="">Any Mood</MenuItem>
                    {MOOD_OPTIONS.filter(m => m).map((mood) => (
                      <MenuItem key={mood} value={mood}>
                        {mood.charAt(0).toUpperCase() + mood.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Energy Range */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Energy Level
                    </Typography>
                    <Switch
                      size="small"
                      checked={energyEnabled}
                      onChange={(e) => setEnergyEnabled(e.target.checked)}
                    />
                  </Box>
                  <Slider
                    value={energyRange}
                    onChange={(_, value) => setEnergyRange(value as [number, number])}
                    valueLabelDisplay="auto"
                    min={1}
                    max={10}
                    disabled={!energyEnabled}
                    marks={[
                      { value: 1, label: 'Low' },
                      { value: 5, label: 'Med' },
                      { value: 10, label: 'High' },
                    ]}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </Collapse>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={clearAllFilters}
              disabled={activeFilterCount === 0}
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setFilterDrawerOpen(false)}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default ContentLibrary;